"use client";

import { useState, useTransition, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Globe, Users, CalendarDays, User, Eye, EyeOff, Pencil, Check, X } from "lucide-react";
import { DiscordLogoIcon } from "@radix-ui/react-icons";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ClanAvatar, DISCORD_REGEX } from "@/components/section-clans/clan-member-view";
import type { Clan, ClanMember } from "@/lib/types";
import { formatDate } from "@/lib/format";
import { fetchClanMembers, updateClan } from "@/query/clans";

type Props = {
  clan: Clan | null;
  isLoggedIn: boolean;
  onClose: () => void;
};

type EditMode = "locked" | "password" | "editing";

export function ClanDetailDialog({ clan, isLoggedIn, onClose }: Props) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const [members, setMembers] = useState<ClanMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  const [editMode, setEditMode] = useState<EditMode>("locked");
  const [unlockPw, setUnlockPw] = useState("");
  const [savedPw, setSavedPw] = useState("");
  const [showUnlockPw, setShowUnlockPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  const [editTag, setEditTag] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editWebsite, setEditWebsite] = useState("");
  const [editDiscord, setEditDiscord] = useState("");
  const [editNewPassword, setEditNewPassword] = useState("");

  useEffect(() => {
    if (!clan) {
      setMembers([]);
      setEditMode("locked");
      setUnlockPw("");
      setSavedPw("");
      return;
    }
    setMembersLoading(true);
    setMembers([]);
    fetchClanMembers(clan.id).then((res) => {
      setMembers(res);
      setMembersLoading(false);
    });
  }, [clan?.id]);

  function handleUnlock() {
    if (!clan || !unlockPw.trim()) return;
    setSavedPw(unlockPw.trim());
    setEditTag(clan.tag ?? "");
    setEditDescription(clan.description ?? "");
    setEditWebsite(clan.website ?? "");
    setEditDiscord(clan.discord ?? "");
    setEditNewPassword("");
    setUnlockPw("");
    setShowUnlockPw(false);
    setEditMode("editing");
  }

  function handleSave() {
    if (!clan) return;
    startTransition(async () => {
      const result = await updateClan(clan.id, savedPw, {
        tag: editTag,
        description: editDescription,
        website: editWebsite,
        discord: editDiscord,
        newPassword: editNewPassword,
      });
      if (result.success) {
        toast.success("Clan aktualisiert.");
        setEditMode("locked");
        setSavedPw("");
        router.refresh();
        onClose();
      } else if (result.reason === "wrong_password") {
        toast.error("Falsches Passwort — bitte erneut entsperren.");
        setEditMode("password");
        setSavedPw("");
      } else if (result.reason === "tag_exists") {
        toast.error("Dieser Clan-Tag wird bereits verwendet.");
      } else {
        toast.error("Fehler beim Speichern.");
      }
    });
  }

  function handleClose() {
    setEditMode("locked");
    setUnlockPw("");
    setSavedPw("");
    onClose();
  }

  if (!clan) return null;

  const count = members.length;

  return (
    <Dialog
      open={!!clan}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <ClanAvatar name={clan.name} size="sm" />
            <span>{clan.name}</span>
            {clan.tag && <span className="text-sm font-mono font-semibold text-muted-foreground">[{clan.tag}]</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-1">
          {/* View mode content */}
          {editMode !== "editing" && (
            <>
              {clan.description && <p className="text-sm text-muted-foreground leading-relaxed">{clan.description}</p>}

              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CalendarDays className="h-4 w-4 shrink-0" />
                  <span>Erstellt am {formatDate(clan.created_at, { month: "long" })}</span>
                </div>
                {clan.creator_name && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <User className="h-4 w-4 shrink-0" />
                    <span>Erstellt von {clan.creator_name}</span>
                  </div>
                )}
                {clan.website && (
                  <div className="flex items-center gap-2 text-sm">
                    <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <a href={clan.website} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline truncate">
                      {clan.website}
                    </a>
                  </div>
                )}
                {clan.discord && (
                  <div className="flex items-center gap-2 text-sm">
                    <DiscordLogoIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <a href={clan.discord} target="_blank" rel="noopener noreferrer" className="text-indigo-500 hover:underline truncate">
                      {clan.discord}
                    </a>
                  </div>
                )}
              </div>

              <Separator />

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    {count} {count === 1 ? "Mitglied" : "Mitglieder"}
                  </span>
                </div>
                {membersLoading ? (
                  <p className="text-xs text-muted-foreground text-center py-3">Laden…</p>
                ) : count === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-3">Noch keine Mitglieder.</p>
                ) : (
                  <ul className="flex flex-col gap-0.5 max-h-48 overflow-y-auto">
                    {members.map((m) => (
                      <li key={m.user_id} className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-muted/50 transition-colors">
                        <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center shrink-0">
                          <span className="text-xs font-semibold text-muted-foreground">{m.user_name.charAt(0).toUpperCase()}</span>
                        </div>
                        <span className="text-sm">{m.user_name}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}

          {/* Edit section */}
          {isLoggedIn && (
            <>
              <Separator />

              {editMode === "locked" && (
                <Button variant="outline" size="sm" className="gap-1.5 self-start" onClick={() => setEditMode("password")}>
                  <Pencil className="h-3.5 w-3.5" /> Bearbeiten
                </Button>
              )}

              {editMode === "password" && (
                <div className="flex flex-col gap-3">
                  <p className="text-sm text-muted-foreground">Clan-Passwort eingeben um Änderungen vorzunehmen:</p>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        type={showUnlockPw ? "text" : "password"}
                        value={unlockPw}
                        onChange={(e) => setUnlockPw(e.target.value)}
                        placeholder="Clan-Passwort"
                        className="pr-9"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleUnlock();
                        }}
                        autoFocus
                      />
                      <button type="button" onClick={() => setShowUnlockPw((p) => !p)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showUnlockPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <Button onClick={handleUnlock} disabled={!unlockPw.trim()}>
                      Entsperren
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditMode("locked");
                        setUnlockPw("");
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {editMode === "editing" && (
                <div className="flex flex-col gap-4">
                  <p className="text-sm font-medium">Clan bearbeiten</p>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="edit-tag">
                      Clan-Tag <span className="text-muted-foreground text-xs">(max. 3 Zeichen, wird als [TAG] angezeigt)</span>
                    </Label>
                    <Input id="edit-tag" value={editTag} onChange={(e) => setEditTag(e.target.value.toUpperCase())} placeholder="z. B. BKL" maxLength={3} className="uppercase" />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="edit-desc">Beschreibung</Label>
                    <Textarea id="edit-desc" value={editDescription} onChange={(e) => setEditDescription(e.target.value)} placeholder="Kurze Beschreibung…" rows={3} />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="edit-website">Webseite</Label>
                    <Input id="edit-website" type="url" value={editWebsite} onChange={(e) => setEditWebsite(e.target.value)} placeholder="https://…" />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="edit-discord">Discord</Label>
                    <Input
                      id="edit-discord"
                      value={editDiscord}
                      onChange={(e) => setEditDiscord(e.target.value)}
                      placeholder="https://discord.gg/…"
                      className={editDiscord && !DISCORD_REGEX.test(editDiscord.trim()) ? "border-red-400 focus-visible:ring-red-400" : ""}
                    />
                    {editDiscord && !DISCORD_REGEX.test(editDiscord.trim()) && <p className="text-xs text-red-500">Ungültiger Discord-Link. Erlaubt: discord.gg/… oder discord.com/invite/…</p>}
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="edit-newpw">
                      Neues Passwort <span className="text-muted-foreground text-xs">(leer = unverändert)</span>
                    </Label>
                    <div className="relative">
                      <Input id="edit-newpw" type={showNewPw ? "text" : "password"} value={editNewPassword} onChange={(e) => setEditNewPassword(e.target.value)} placeholder="Neues Clan-Passwort" className="pr-9" />
                      <button type="button" onClick={() => setShowNewPw((p) => !p)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={() => setEditMode("locked")} disabled={isPending}>
                      <X className="h-3.5 w-3.5 mr-1.5" /> Abbrechen
                    </Button>
                    <Button onClick={handleSave} disabled={isPending || !editTag.trim() || (!!editDiscord && !DISCORD_REGEX.test(editDiscord.trim()))}>
                      <Check className="h-3.5 w-3.5 mr-1.5" /> Speichern
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
